package com.solevo.dist.pages.productdetails

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.solevo.dist.navigation.NavigationConstants.PRODUCT_ID_PARAM
import com.solevo.dist.viewmodels.ProductDetailsViewModel
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.solevo.dist.i18n.LocalStrings
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.percent
import org.w3c.dom.url.URLSearchParams
import com.solevo.dist.util.Res
import com.varabyte.kobweb.compose.ui.styleModifier

@Page("/productdetails/product")
@Composable
fun ProductDetailsScreen() {
    RequireAuth { ProductDetailsPage() }
}

@Composable
fun ProductDetailsPage() {
    val productId = remember {
        val params = URLSearchParams(window.location.search)
        params.get(PRODUCT_ID_PARAM) ?: ""
    }

    WaitForAuthAndViewModel(
        key = productId,
        viewModelFactory = { ProductDetailsViewModel(productId) },
        onViewModelReady = { vm -> vm.start(productId) }
    ) { vm ->
        val ui by vm.state.collectAsState()

        DashboardPageLayout {
            Box(Modifier.fillMaxWidth().padding(24.px)) {
                when {
                    ui.loading -> SpanText(LocalStrings.current.loading)
                    ui.error != null -> SpanText("${LocalStrings.current.errorPrefix}: ${ui.error}")
                    else -> ui.product?.let { p -> ProductDetailsContent(p) } ?: SpanText(LocalStrings.current.noData)
                }
            }
        }
    }
}

@Composable
private fun ProductDetailsContent(product: com.solevo.dist.application.models.Product) {
    val context = rememberPageContext()
    val contentLeftIndent = 40.px // arrow (~24px) + gap (16px)
    Column(Modifier.fillMaxWidth().gap(24.px)) {
        // Header with image placeholder and name
        Row(
            Modifier.fillMaxWidth().gap(16.px).alignItems(AlignItems.Start)
        ) {
            FaChevronLeft(
                modifier = Modifier
                    .margin(top = 4.px) // nudge up to align top edge with image
                    .cursor(Cursor.Pointer)
                    .onClick { window.history.back() },
                size = IconSize.LG
            )
            Column(Modifier.gap(8.px)) {
                Box(
                    Modifier.size(76.px)
                        .border(1.px, LineStyle.Solid, rgb(230, 232, 235))
                        .borderRadius(12.px)
                        .backgroundColor(rgb(245, 246, 248))
                ) {
                    val primaryFromList = product.productImagesFileNames?.firstOrNull().orEmpty()
                    val imgSrc = if (primaryFromList.isNotBlank()) primaryFromList else (product.imageUrl ?: Res.Icon.image)
                    Image(
                        modifier = Modifier
                            .width(100.percent)
                            .height(100.percent)
                            .borderRadius(12.px)
                            .styleModifier {
                                property("object-fit", "cover")
                                property("object-position", "center")
                            },
                        src = imgSrc,
                        alt = "Product image"
                    )
                }
                SpanText(product.getProductName(), Modifier.fontSize(28.px).fontWeight(FontWeight.Bold))
                SpanText(product.categoryName ?: "", Modifier.color(rgb(120, 130, 140)))
            }
        }

        // Product Information section
        Column(Modifier.fillMaxWidth().gap(18.px).margin(left = contentLeftIndent)) {
            SectionTitle(LocalStrings.current.productInformationTitle)
            InfoGrid(
                leftTitle = LocalStrings.current.packaging,
                leftValue = listOfNotNull(product.details?.productInformation?.conditioning, product.details?.productInformation?.conditioningUnit).joinToString(" ").ifBlank { "—" },
                rightTitle = LocalStrings.current.productFamily,
                rightValue = product.details?.productInformation?.family ?: "—"
            )
            InfoRow(LocalStrings.current.registrationNo, product.details?.productInformation?.approvalNumber ?: "—")
            InfoRow(LocalStrings.current.productDetailsLabel, product.details?.productInformation?.details ?: "—")
            InfoRow(LocalStrings.current.generalScope, product.details?.productInformation?.generalScope ?: "—")
        }

        // Ingredients section
        Column(Modifier.fillMaxWidth().gap(18.px).margin(left = contentLeftIndent)) {
            SectionTitle(LocalStrings.current.ingredientsTitle)
            InfoGrid(
                leftTitle = LocalStrings.current.activeIngredients,
                leftValue = product.details?.ingredients?.activeIngredients ?: "—",
                rightTitle = LocalStrings.current.formulation,
                rightValue = product.details?.ingredients?.formulation ?: "—"
            )
            InfoRow(LocalStrings.current.chemicalFamily, product.details?.ingredients?.chemicalFamily ?: "—")
        }

        // Preparation section
        Column(Modifier.fillMaxWidth().gap(18.px).margin(left = contentLeftIndent)) {
            SectionTitle(LocalStrings.current.preparationTitle)
            InfoRow(LocalStrings.current.directionsForUse, product.details?.preparation?.preparation ?: "—")
            val afterglow = product.details?.preparation?.afterglow
            if (!afterglow.isNullOrBlank()) InfoRow(LocalStrings.current.afterglow, afterglow)
            val preHarvest = product.details?.preparation?.preHarvestInterval
            if (!preHarvest.isNullOrBlank()) InfoRow(LocalStrings.current.timeToHarvest, preHarvest)
            val reentry = product.details?.preparation?.reEntryPeriod
            if (!reentry.isNullOrBlank()) InfoRow(LocalStrings.current.reEntryPeriod, reentry)
        }

        // Safety section
        Column(Modifier.fillMaxWidth().gap(18.px).margin(left = contentLeftIndent)) {
            SectionTitle(LocalStrings.current.safetyTitle)
            InfoRow(LocalStrings.current.toxicity, product.details?.safety?.toxicity ?: "—")
            InfoRow(LocalStrings.current.safetyPhrases, product.details?.safety?.safetyPhrases ?: "—")
            InfoRow(LocalStrings.current.firstCareDoctorInfo, product.details?.safety?.careAndInfo ?: "—")
        }

        // Application section
        Column(Modifier.fillMaxWidth().gap(18.px).margin(left = contentLeftIndent)) {
            SectionTitle(LocalStrings.current.applicationTitle)
            InfoGrid(
                leftTitle = LocalStrings.current.cultures,
                leftValue = product.details?.application?.crops ?: "—",
                rightTitle = LocalStrings.current.targetRegistration,
                rightValue = product.details?.application?.approvalTarget ?: "—"
            )
            InfoRow(LocalStrings.current.applicationRates, product.details?.application?.dose ?: "—")
            InfoRow(LocalStrings.current.applicationPeriod, product.details?.application?.period ?: "—")
            InfoRow(LocalStrings.current.moreDetailedModeOfAction, product.details?.application?.details ?: "—")
            InfoRow(LocalStrings.current.productStrengths, product.details?.application?.highlights ?: "—")
        }
    }
}

@Composable
private fun InfoGrid(leftTitle: String, leftValue: String, rightTitle: String, rightValue: String) {
    Row(Modifier.fillMaxWidth().gap(36.px)) {
        Column(Modifier.flex(1, 1, 0.px).gap(6.px)) {
            LabelSmall(leftTitle)
            SpanText(leftValue)
        }
        Column(Modifier.flex(1, 1, 0.px).gap(6.px)) {
            LabelSmall(rightTitle)
            SpanText(rightValue)
        }
    }
}

@Composable
private fun InfoRow(title: String, value: String) {
    Column(Modifier.fillMaxWidth().gap(6.px)) {
        LabelSmall(title)
        SpanText(value)
    }
}

@Composable
private fun LabelSmall(text: String) {
    SpanText(text, Modifier.fontSize(12.px).color(rgb(120, 130, 140)))
}

@Composable
private fun SectionTitle(text: String) {
    SpanText(text, Modifier.fontSize(18.px).fontWeight(FontWeight.SemiBold))
}


